// KidBankCard — small piggy-bank tile on the kid Today screen.
// Shows the kid's $ balance + a Cash out button that opens the
// shared CashOutRequestModal. Hidden entirely if the kid hasn't
// earned any stars yet AND has no pending request (no signal
// for the visual to surface).

import { useEffect, useState } from "preact/hooks";
import { api, type BankMyPending, type FamilyStarsMember } from "../../lib/rpc";
import { CashOutRequestModal } from "../chores/bank";
import type { Me } from "../../lib/use-me";
import "./bank-card.css";

interface Props {
  me: Me;
  notify: (msg: string) => void;
  /** Bumped by parent on state changes (chore completion, etc.) so the
   *  card re-fetches balance + pending. */
  refreshToken?: number;
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function KidBankCard({ me, notify, refreshToken = 0 }: Props) {
  const [member, setMember] = useState<FamilyStarsMember | null>(null);
  const [rate, setRate] = useState<number | null>(null);
  const [enabled, setEnabled] = useState<boolean>(true);
  const [myPending, setMyPending] = useState<BankMyPending | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.householdsStarsByMember().catch(() => [] as FamilyStarsMember[]),
      api.bankConfig().catch(() => ({ rate: 10, enabled: true })),
      api.bankMyPending().catch(() => null),
    ]).then(([members, cfg, mine]) => {
      if (cancelled) return;
      setMember(members.find((m) => m.userId === me.username) || null);
      setRate(cfg.rate);
      setEnabled(cfg.enabled);
      setMyPending(mine);
    });
    return () => { cancelled = true; };
  }, [refreshToken, bump, me.username]);

  if (!member || rate === null) return null;
  // Bank turned off by the parent — hide the card entirely. Pending
  // requests stay on the server; they'll re-surface if re-enabled.
  if (!enabled) return null;

  // Skip the card when the kid has nothing to look at — no spendable
  // stars, no bank balance, no in-flight request. Otherwise it's a
  // confusing empty piggy.
  const hasSomething = member.cashableStars >= rate
    || member.dollarsCents > 0
    || myPending !== null;
  if (!hasSomething) return null;

  return (
    <section className="kid-bank-card" data-testid="kid-bank-card">
      <div className="kid-bank-head">
        <span className="kid-bank-piggy" aria-hidden>🐷</span>
        <div className="kid-bank-titles">
          <div className="kid-bank-label">My piggy bank</div>
          <div className="kid-bank-amount" data-testid="kid-bank-amount">
            {dollars(member.dollarsCents)}
          </div>
        </div>
      </div>
      {myPending ? (
        <div className="kid-bank-pending" data-testid="kid-bank-pending">
          Waiting on a grown-up · {myPending.starsAmount}⭐ → {dollars(myPending.dollarsCents)}
        </div>
      ) : (
        <button
          type="button"
          className="kid-bank-cashout"
          onClick={() => setRequestOpen(true)}
          disabled={member.cashableStars < rate}
          data-testid="kid-bank-cashout"
        >
          {member.cashableStars >= rate
            ? `Cash out ⭐ (${member.cashableStars} ready)`
            : `Need ${rate}⭐ to cash out`}
        </button>
      )}
      {requestOpen && (
        <CashOutRequestModal
          member={member}
          rate={rate}
          notify={notify}
          onClose={() => setRequestOpen(false)}
          onSubmitted={() => { setRequestOpen(false); setBump((n) => n + 1); }}
        />
      )}
    </section>
  );
}
